import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  ANTWORT_HOECHSTLAENGE,
  SENDEZUSTANDNAME,
  anbindung as ladeAnbindung,
  antwortLoeschen,
  antworten,
  antwortlaenge,
  erneutSenden,
  rezension as ladeRezension,
  rezensionen as ladeRezensionen,
  type Anbindungsstand,
  type Antwortstand,
  type Rezension,
  type Sendeauftrag,
} from '../api/rezensionen';
import {
  FUNKTION_REZENSIONSANTWORT,
  kiStand,
  rezensionsentwurfEntscheiden,
  rezensionsentwurfErzeugen,
  type KiVorschlag,
  type Rezensionsentwurf,
} from '../api/ki';
import { ApiFehler } from '../api/fehler';
import { Kikennzeichnung } from '../bericht/KiBericht';
import { markenklasse } from '../marken';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Die Google-Rezensionen im Service (SUP-50 bis SUP-55, AeA-51).
 *
 * SEITENTYP LISTE (ÄA-41): die Rezensionen in einer Tabelle, die Antwortmaske
 * in der Seitenleiste. Die Vorgabe ist „offen" (SUP-53) — wer die Seite
 * öffnet, will wissen, was noch zu beantworten ist.
 *
 * EINE ANTWORT GEHT NICHT SOFORT HINAUS. Sie wird in den Ausgang gelegt, und
 * der Rezensionslauf stellt sie zu (SUP-52). Deshalb zeigt die Maske den
 * Sendezustand und nicht „gespeichert": Erst „gesendet" heißt, dass Google sie
 * hat. Was scheitert, lässt sich hier erneut anstoßen.
 */

/** SUP-53: die Sternwahl — einzeln oder die kritischen zusammen. */
const STERNWAHL: Readonly<Record<string, readonly number[]>> = {
  alle: [],
  kritisch: [1, 2],
  '1': [1],
  '2': [2],
  '3': [3],
  '4': [4],
  '5': [5],
};

function datum(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

function sterne(anzahl: number): string {
  return '★'.repeat(anzahl) + '☆'.repeat(5 - anzahl);
}

/** SUP-52: der Sendezustand als Marke — was scheiterte, fällt auf. */
function sendemarke(auftrag: Sendeauftrag): JSX.Element {
  const art =
    auftrag.zustand === 'gesendet'
      ? 'abschluss'
      : auftrag.zustand === 'aufgegeben'
        ? 'sackgasse'
        : auftrag.zustand === 'fehlgeschlagen'
          ? 'aufmerksamkeit'
          : 'laufend';
  const was = auftrag.vorgang === 'antwort_loeschen' ? 'Löschen' : 'Antwort';
  return <span className={markenklasse(art)}>{`${was}: ${SENDEZUSTANDNAME[auftrag.zustand]}`}</span>;
}

function antwortmarke(r: Rezension): JSX.Element {
  return r.antwortText === null ? (
    <span className={markenklasse('aufmerksamkeit')}>offen</span>
  ) : (
    <span className={markenklasse('abschluss')}>beantwortet</span>
  );
}

export function Rezensionen({
  darfBeantworten,
}: {
  /** `rezension/beantworten` (SUP-54). Ohne das Recht: nur lesen. */
  readonly darfBeantworten: boolean;
}): JSX.Element {
  const [stand, setzeStand] = useState<Antwortstand>('offen');
  const [standort, setzeStandort] = useState<string | null>(null);
  const [sternwahl, setzeSternwahl] = useState('alle');
  const [liste, setzeListe] = useState<readonly Rezension[] | null>(null);
  const [gesamt, setzeGesamt] = useState(0);
  const [anbindung, setzeAnbindung] = useState<Anbindungsstand | null>(null);
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const fange = useCallback((f: unknown) => {
    setzeFehler(f instanceof ApiFehler ? f : null);
    if (!(f instanceof ApiFehler)) throw f;
  }, []);

  const holen = useCallback(async () => {
    try {
      const antwort = await ladeRezensionen({ standort, sterne: STERNWAHL[sternwahl] ?? [], stand });
      setzeListe(antwort.rezensionen);
      setzeGesamt(antwort.gesamt);
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      fange(f);
    }
  }, [standort, sternwahl, stand, fange]);

  useEffect(() => {
    void holen();
  }, [holen]);

  useEffect(() => {
    ladeAnbindung().then(setzeAnbindung, fange);
  }, [fange]);

  useZaehler(liste === null ? undefined : zaehlwort(gesamt, 'Rezension', 'Rezensionen'));

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        vorschauBeschriftung="Antworten"
        filter={
          <>
            <Segmente
              beschriftung="Stand"
              gewaehlt={stand}
              beiWahl={setzeStand}
              optionen={[
                { wert: 'offen', text: 'offen' },
                { wert: 'beantwortet', text: 'beantwortet' },
                { wert: 'alle', text: 'alle' },
              ]}
            />
            <select
              className="liste__filter"
              value={standort ?? ''}
              onChange={(e) => {
                setzeStandort(e.target.value === '' ? null : e.target.value);
              }}
              aria-label="Standort"
            >
              <option value="">Alle Standorte</option>
              {(anbindung?.standorte ?? []).map((s) => (
                <option key={s.kennung} value={s.kennung}>
                  {s.bezeichnung}
                </option>
              ))}
            </select>
            <select
              className="liste__filter"
              value={sternwahl}
              onChange={(e) => {
                setzeSternwahl(e.target.value);
              }}
              aria-label="Sterne"
            >
              <option value="alle">Alle Sterne</option>
              <option value="kritisch">1 und 2 Sterne</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n === 1 ? '1 Stern' : `${String(n)} Sterne`}
                </option>
              ))}
            </select>
          </>
        }
        hinweise={
          <>
            <p className="hilfe">
              Die Rezensionen aus dem Google-Unternehmensprofil, alle 15 Minuten abgeglichen. Offen
              ist, was noch keine Antwort trägt. Eine Antwort wird erst gesendet und dann bei Google
              sichtbar — der Sendezustand steht in der Zeile.
            </p>
            {/* SUP-55: Gegen die Attrappe ist alles erfunden, und nichts geht hinaus. */}
            {anbindung?.adapter === 'attrappe' && (
              <p className="warnung" role="status">
                <strong>Attrappe — keine Verbindung zu Google.</strong> Die Rezensionen auf dieser
                Seite sind erfunden, und Antworten gehen nicht an Google. So lässt sich alles
                ausprobieren, solange kein Google-Unternehmensprofil angebunden ist.
              </p>
            )}
            {anbindung?.adapter === 'gesperrt' && (
              <p className="warnung" role="status">
                <strong>Google ist nicht angebunden.</strong> Für diese Instanz ist kein
                Google-Unternehmensprofil eingerichtet. Rezensionen werden nicht abgeglichen, und
                Antworten lassen sich hier nicht senden.
              </p>
            )}
          </>
        }
        vorschau={
          <Antwortmaske
            id={gewaehlt}
            darfBeantworten={darfBeantworten && anbindung?.adapter !== 'gesperrt'}
            attrappe={anbindung?.adapter === 'attrappe'}
            beiSchliessen={() => {
              setzeGewaehlt(null);
            }}
            beiAenderung={() => void holen()}
          />
        }
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && liste.length === 0 && (
          <p className="liste__leer">
            {stand === 'offen' ? 'Keine Rezension wartet auf eine Antwort.' : 'Keine Rezension passt zum Filter.'}
          </p>
        )}
        {liste !== null && liste.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Rezensionen">
              <thead>
                <tr>
                  <th scope="col">Verfasst</th>
                  <th scope="col">Sterne</th>
                  <th scope="col">Rezension</th>
                  <th scope="col">Standort</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((r) => (
                  <tr key={r.id} className={gewaehlt === r.id ? 'liste__zeile--gewaehlt' : undefined}>
                    <th scope="row">
                      <button
                        type="button"
                        className="liste__oeffnen"
                        onClick={() => {
                          setzeGewaehlt(r.id);
                        }}
                      >
                        <span className="liste__einzeilig">{datum(r.erstelltAm)}</span>
                      </button>
                    </th>
                    <td aria-label={`${String(r.sterne)} von 5 Sternen`}>{sterne(r.sterne)}</td>
                    <td>
                      <span className="liste__name">{r.verfasser}</span>
                      <br />
                      <span className="hilfe">{r.text ?? 'ohne Text, nur Sterne'}</span>
                    </td>
                    <td>{r.standortBezeichnung}</td>
                    <td>
                      {antwortmarke(r)}
                      {r.auftrag !== null && r.auftrag.zustand !== 'gesendet' && (
                        <>
                          <br />
                          {sendemarke(r.auftrag)}
                        </>
                      )}
                      {/* SUP-50: nach der Antwort geändert — die Antwort passt womöglich nicht mehr. */}
                      {r.aenderungErkanntAm !== null && (
                        <>
                          <br />
                          <span className="hilfe">geändert am {datum(r.aenderungErkanntAm)}</span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>
    </>
  );
}

/** KI-07: ob der KI-Entwurf geht — und wenn nicht, warum. */
type Kibereitschaft = { readonly verfuegbar: true } | { readonly verfuegbar: false; readonly hinweis: string };

/**
 * DIE ANTWORTMASKE (SUP-52): die Rezension, die Antwort mit Zeichenzähler und
 * der Sendezustand samt Verlauf.
 *
 * DER KI-ENTWURF (SUP-56 bis SUP-59): Ein Nebenknopf füllt das Antwortfeld
 * mit einem gekennzeichneten Entwurf. Steht schon Text im Feld, fragt die
 * Maske, bevor sie ihn ersetzt. Gesendet wird nur über „Antwort senden";
 * danach hält die Maske fest, was aus dem Entwurf wurde — derselbe Text
 * zählt als übernommen, ein bearbeiteter als geändert (KI-04, KI-08). Wer
 * den Entwurf verwirft, eine andere Rezension öffnet oder die Seite
 * verlässt, verwirft ihn.
 *
 * SENDEN, ÄNDERN UND LÖSCHEN FRAGEN NACH (CRM-104): Sie gehen nach außen und
 * stehen öffentlich unter der Rezension. „Erneut senden" fragt nicht — es
 * stellt zu, was schon bestätigt war und nie ankam.
 */
function Antwortmaske({
  id,
  darfBeantworten,
  attrappe,
  beiSchliessen,
  beiAenderung,
}: {
  readonly id: string | null;
  readonly darfBeantworten: boolean;
  /** SUP-55: Gegen die Attrappe geht nichts an Google — die Rückfrage sagt es. */
  readonly attrappe: boolean;
  readonly beiSchliessen: () => void;
  readonly beiAenderung: () => void;
}): JSX.Element {
  const [daten, setzeDaten] = useState<{
    readonly rezension: Rezension;
    readonly verlauf: readonly Sendeauftrag[];
  } | null>(null);
  const [entwurf, setzeEntwurf] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [ki, setzeKi] = useState<Kibereitschaft | null>(null);
  const [vorschlag, setzeVorschlag] = useState<KiVorschlag<Rezensionsentwurf> | null>(null);
  /** Der Text vor dem Entwurf — „Entwurf verwerfen" stellt ihn wieder her. */
  const [vorher, setzeVorher] = useState('');
  const [ersetzenFragen, setzeErsetzenFragen] = useState(false);
  const [rueckfrage, setzeRueckfrage] = useState<'senden' | 'loeschen' | null>(null);
  /* Die Aufräumfunktion des Effekts sieht den Zustand nicht; der Verweis hält den offenen Entwurf. */
  const offenerEntwurf = useRef<string | null>(null);

  const merkeVorschlag = (v: KiVorschlag<Rezensionsentwurf> | null): void => {
    offenerEntwurf.current = v?.id ?? null;
    setzeVorschlag(v);
  };

  // KI-07: einmal je Maske; ohne Antwort des Servers gilt die KI als nicht verfügbar.
  useEffect(() => {
    if (!darfBeantworten) return;
    kiStand()
      .then((stand) => {
        const f = stand.find((s) => s.funktion === FUNKTION_REZENSIONSANTWORT);
        setzeKi(f?.verfuegbarkeit ?? { verfuegbar: false, hinweis: 'Die Funktion ist nicht eingerichtet.' });
      })
      .catch(() => setzeKi({ verfuegbar: false, hinweis: 'Der Stand der KI ließ sich nicht abfragen.' }));
  }, [darfBeantworten]);

  const holen = useCallback(async (kennung: string) => {
    const geladen = await ladeRezension(kennung);
    setzeDaten(geladen);
    const offen = geladen.rezension.auftrag;
    setzeEntwurf(
      offen !== null && offen.zustand !== 'gesendet' && offen.vorgang === 'antwort_setzen'
        ? (offen.antwortText ?? '')
        : (geladen.rezension.antwortText ?? ''),
    );
  }, []);

  useEffect(() => {
    setzeDaten(null);
    setzeFehler(null);
    setzeMeldung(null);
    setzeVorschlag(null);
    setzeErsetzenFragen(false);
    setzeRueckfrage(null);
    if (id === null) return;
    holen(id).catch((f: unknown) => {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    });
    // KI-04: Ein Entwurf, den niemand gesendet hat, ist verworfen.
    return () => {
      const offen = offenerEntwurf.current;
      offenerEntwurf.current = null;
      if (offen !== null) void rezensionsentwurfEntscheiden(offen, { art: 'verwerfen' }).catch(() => undefined);
    };
  }, [id, holen]);

  const tue = async (was: () => Promise<string>): Promise<void> => {
    if (id === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      setzeMeldung(await was());
      await holen(id);
      beiAenderung();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  /**
   * Aus der Rückfrage (CRM-104): Scheitert der Auftrag, wirft er — der Fehler
   * steht im Pop-up, und es bleibt offen. Erst danach schließt es, und die
   * Maske lädt den Stand wie nach jedem anderen Auftrag.
   */
  const bestaetigt = async (was: () => Promise<string>): Promise<void> => {
    const text = await was();
    setzeRueckfrage(null);
    await tue(() => Promise.resolve(text));
  };

  if (id === null) {
    return (
      <p className="feld__hilfe">
        Eine Rezension in der Liste öffnen, um sie zu lesen und zu beantworten.
      </p>
    );
  }
  if (daten === null) {
    return fehler === null ? <p role="status">Wird geladen …</p> : <Meldung fehler={fehler} />;
  }

  const { rezension: r, verlauf } = daten;
  const laenge = antwortlaenge(entwurf);
  const zuLang = laenge > ANTWORT_HOECHSTLAENGE;
  const offen = r.auftrag;
  const wiederholbar = offen !== null && (offen.zustand === 'fehlgeschlagen' || offen.zustand === 'aufgegeben');
  const loeschbar = r.antwortText !== null || (offen?.vorgang === 'antwort_setzen' && offen.zustand !== 'gesendet');

  /** SUP-56: Ein offener Entwurf wird vor dem neuen verworfen; der Text davor bleibt für „Entwurf verwerfen". */
  const entwurfErstellen = async (): Promise<void> => {
    setzeErsetzenFragen(false);
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      if (vorschlag !== null) {
        await rezensionsentwurfEntscheiden(vorschlag.id, { art: 'verwerfen' });
        merkeVorschlag(null);
      } else {
        setzeVorher(entwurf);
      }
      const v = await rezensionsentwurfErzeugen(r.id);
      merkeVorschlag(v);
      setzeEntwurf(v.entwurf.text);
      setzeMeldung('Der KI-Entwurf steht im Antwortfeld. Bitte prüfen und bearbeiten; gesendet wird erst mit „Antwort senden".');
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  const entwurfVerwerfen = async (): Promise<void> => {
    if (vorschlag === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      await rezensionsentwurfEntscheiden(vorschlag.id, { art: 'verwerfen' });
      merkeVorschlag(null);
      setzeEntwurf(vorher);
      setzeMeldung('Der KI-Entwurf ist verworfen.');
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  /** SUP-52: Die Antwort geht in den Ausgang; KI-04, KI-08: danach steht fest, was aus dem Entwurf wurde. */
  const senden = async (): Promise<string> => {
    await antworten(r.id, entwurf);
    if (vorschlag === null) return 'Die Antwort ist zum Senden vorgemerkt.';
    // KI-04, KI-08: Gesendet ist, was der Anwender abgeschickt hat — mit oder ohne Änderung.
    const kennung = vorschlag.id;
    merkeVorschlag(null);
    try {
      await rezensionsentwurfEntscheiden(kennung, { art: 'uebernehmen', text: entwurf });
      return 'Die Antwort ist zum Senden vorgemerkt.';
    } catch {
      return 'Die Antwort ist zum Senden vorgemerkt. Was aus dem KI-Entwurf wurde, ließ sich nicht festhalten.';
    }
  };

  /** SUP-56: Steht eigener Text im Feld, wird er nicht stillschweigend ersetzt. */
  const kiKnopf = (): void => {
    const eigenerText = entwurf.trim() !== '' && entwurf !== vorschlag?.entwurf.text;
    if (eigenerText) setzeErsetzenFragen(true);
    else void entwurfErstellen();
  };

  return (
    <>
      <div className="karte__titel">
        <h2>{r.verfasser}</h2>
        <small>{r.standortBezeichnung}</small>
      </div>
      <p aria-label={`${String(r.sterne)} von 5 Sternen`}>{sterne(r.sterne)}</p>
      <p className="hilfe">
        verfasst am {datum(r.erstelltAm)}
        {r.geaendertAm !== r.erstelltAm && <> · geändert am {datum(r.geaendertAm)}</>}
      </p>
      <p>{r.text ?? 'Der Verfasser hat nur Sterne vergeben, keinen Text.'}</p>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {offen !== null && (
        <p>
          {sendemarke(offen)}
          {offen.zustand === 'fehlgeschlagen' && offen.naechsterVersuch !== null && (
            <span className="hilfe"> nächster Versuch {datum(offen.naechsterVersuch)}</span>
          )}
          {offen.letzterFehler !== null && offen.zustand !== 'gesendet' && (
            <>
              <br />
              <span className="hilfe">{offen.letzterFehler}</span>
            </>
          )}
        </p>
      )}

      {darfBeantworten ? (
        <div className="feld">
          <label htmlFor="rezension-antwort">Antwort</label>
          <textarea
            id="rezension-antwort"
            rows={6}
            value={entwurf}
            aria-invalid={zuLang}
            aria-describedby="rezension-antwort-zaehler"
            onChange={(e) => {
              setzeEntwurf(e.target.value);
            }}
          />
          {/* SUP-52: die Grenze von Google, vor dem Senden sichtbar. */}
          <span id="rezension-antwort-zaehler" className={zuLang ? 'feld__fehler' : 'feld__hilfe'}>
            {zuLang
              ? `${String(laenge)} von ${String(ANTWORT_HOECHSTLAENGE)} Zeichen — bitte um ${String(laenge - ANTWORT_HOECHSTLAENGE)} kürzen.`
              : `${String(laenge)} von ${String(ANTWORT_HOECHSTLAENGE)} Zeichen`}
          </span>
          {/* KI-01: Der Entwurf bleibt gekennzeichnet, solange er nicht gesendet ist. */}
          {vorschlag !== null && <Kikennzeichnung kennzeichnung={vorschlag.kennzeichnung} />}
          {ersetzenFragen ? (
            <div className="hinweiskarte" role="alertdialog" aria-label="Text ersetzen?">
              <p>Im Antwortfeld steht schon Text. Durch einen KI-Entwurf ersetzen?</p>
              <div className="knopfreihe">
                <button type="button" className="knopf knopf--klein" onClick={() => setzeErsetzenFragen(false)}>
                  Abbrechen
                </button>
                <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void entwurfErstellen()}>
                  Text ersetzen
                </button>
              </div>
            </div>
          ) : (
            <div className="knopfreihe">
              {/* SUP-59, KI-07: gesperrt mit Grund, wenn die Funktion nicht zur Verfügung steht. */}
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={laeuft || ki?.verfuegbar !== true}
                aria-describedby={ki?.verfuegbar === false ? 'rezension-ki-grund' : undefined}
                onClick={kiKnopf}
              >
                {vorschlag === null ? 'KI-Entwurf erstellen' : 'Neuen KI-Entwurf erstellen'}
              </button>
              {vorschlag !== null && (
                <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void entwurfVerwerfen()}>
                  Entwurf verwerfen
                </button>
              )}
            </div>
          )}
          {ki?.verfuegbar === false && (
            <span id="rezension-ki-grund" className="feld__hilfe">
              KI-Entwurf nicht verfügbar: {ki.hinweis}
            </span>
          )}
        </div>
      ) : (
        <p>{r.antwortText ?? <span className="hilfe">Noch keine Antwort.</span>}</p>
      )}

      {darfBeantworten && (
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf knopf--klein" onClick={beiSchliessen}>
              Schließen
            </button>
          }
        >
          {loeschbar && (
            <button
              type="button"
              className="knopf knopf--eingriff knopf--klein"
              disabled={laeuft}
              onClick={() => setzeRueckfrage('loeschen')}
            >
              Antwort löschen …
            </button>
          )}
          {wiederholbar && (
            <button
              type="button"
              className="knopf knopf--klein"
              disabled={laeuft}
              onClick={() =>
                void tue(async () => {
                  await erneutSenden(offen.id);
                  return 'Der Auftrag wird erneut gesendet.';
                })
              }
            >
              Erneut senden
            </button>
          )}
          <button
            type="button"
            className="knopf knopf--haupt knopf--klein"
            disabled={laeuft || zuLang || laenge === 0}
            onClick={() => setzeRueckfrage('senden')}
          >
            {r.antwortText === null ? 'Antwort senden …' : 'Antwort ändern …'}
          </button>
        </Abschlussreihe>
      )}

      {rueckfrage === 'senden' && (
        <Rueckfrage
          frage={r.antwortText === null ? 'Antwort senden?' : 'Antwort ändern?'}
          unterzeile={`${r.verfasser} · ${String(r.sterne)} von 5 Sternen · ${r.standortBezeichnung}`}
          tat={r.antwortText === null ? 'Senden' : 'Ändern'}
          beiBestaetigen={() => bestaetigt(senden)}
          beiSchliessen={() => setzeRueckfrage(null)}
        >
          <p>
            {attrappe
              ? 'Die Anbindung läuft gegen die Attrappe: Die Antwort geht nicht an Google.'
              : r.antwortText === null
                ? 'Die Antwort erscheint bei Google öffentlich unter der Rezension.'
                : 'Die neue Fassung ersetzt die Antwort, die bei Google öffentlich unter der Rezension steht.'}{' '}
            {/* KI-02: Hinaus geht der Entwurf nur so, wie der Anwender ihn hier bestätigt. */}
            {vorschlag !== null && 'Der Text stammt aus einem KI-Entwurf und geht so hinaus, wie er im Antwortfeld steht.'}
          </p>
        </Rueckfrage>
      )}
      {rueckfrage === 'loeschen' && (
        <Rueckfrage
          frage="Antwort löschen?"
          unterzeile={`${r.verfasser} · ${String(r.sterne)} von 5 Sternen · ${r.standortBezeichnung}`}
          tat="Löschen"
          beiBestaetigen={() =>
            bestaetigt(async () => {
              await antwortLoeschen(r.id);
              return 'Die Antwort wird bei Google gelöscht.';
            })
          }
          beiSchliessen={() => setzeRueckfrage(null)}
        >
          <p>
            {r.antwortText === null
              ? 'Die vorgemerkte Antwort geht nicht an Google.'
              : attrappe
                ? 'Die Anbindung läuft gegen die Attrappe: Bei Google ändert sich nichts.'
                : 'Die Antwort wird bei Google gelöscht; unter der Rezension steht dann keine Antwort mehr.'}
          </p>
        </Rueckfrage>
      )}

      {verlauf.length > 0 && (
        <>
          <h3>Verlauf</h3>
          <ul className="kanalliste" aria-label="Sendeverlauf">
            {verlauf.map((a) => (
              <li key={a.id}>
                {datum(a.angelegtAm)} · {a.angelegtVon ?? 'unbekannt'} · {sendemarke(a)}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
